import { Container, Modal, TabItemProps, Tabs } from '@/components';
import { useAttachUserTask } from '@/modules/Tasks/queries/attach-user-task';
import { useCreateUser } from '@/modules/Users/queries/create-user';
import { UserSchemaType } from '@/modules/Users/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AddCsvTabModal } from '../AddCsvTab';
import { AssociateUserTab } from '../AssociateUserTab/AssociateUserTab';
import { UserModal } from '../UserModal';

type CreateTaskUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  taskId: number;
  businessAreaId?: number;
};

export const CreateTaskUserModal = ({
  isOpen,
  onClose,
  taskId,
  businessAreaId,
}: CreateTaskUserModalProps) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { mutate: attachUserTask, isLoading: isLoadingAttachUserCompany } =
    useAttachUserTask();

  const onSubmitUserAssociation = useCallback(
    (userIds: number[]) => {
      attachUserTask(
        {
          taskId,
          userIds,
        },
        {
          onError: () => {
            toast.error(t('error.associate_user_company'));
          },
          onSuccess: () => {
            onClose();
          },
        },
      );
    },
    [attachUserTask, onClose, t, taskId],
  );

  const { mutate: createUser, isLoading: IsLoadingCreateUser } = useCreateUser(
    ({ data: { userId } }) => {
      onSubmitUserAssociation([userId]);
      toast.success(t('success_creating_user'));
    },
  );

  const onSubmitUserCreate = useCallback(
    async (user: UserSchemaType) => {
      createUser({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        status: user.status,
        access: user.access,
      });
    },
    [createUser],
  );

  const tabs: Array<TabItemProps> = useMemo(
    () => [
      { label: t('create_new') },
      {
        label: t('add_through_csv'),
      },
      {
        label: t('add_existing_users'),
      },
    ],
    [t],
  );

  const modalTitle = selectedTab !== 2 ? 'create_users' : 'add_users';

  return (
    <Modal
      className="h-fit w-128"
      isOpen={isOpen}
      title={t(modalTitle)}
      handleClose={onClose}
    >
      <div
        data-testid={'create_task_user_modal'}
        className="mt-6 flex flex-col"
      >
        <Container>
          <Tabs
            activeTab={selectedTab}
            tabs={tabs}
            onTabChange={setSelectedTab}
          />
        </Container>
        {selectedTab === 0 && (
          <UserModal
            isLoading={IsLoadingCreateUser || isLoadingAttachUserCompany}
            handleOnSubmit={onSubmitUserCreate}
            handleClose={onClose}
            type="create"
          />
        )}
        {selectedTab === 1 && (
          <AddCsvTabModal taskId={taskId} handleClose={onClose} />
        )}
        {selectedTab === 2 && (
          <AssociateUserTab
            businessAreaId={businessAreaId}
            onClose={onClose}
            onSubmit={(items) =>
              onSubmitUserAssociation(items.map((item) => item.id))
            }
            isSubmitting={isLoadingAttachUserCompany}
          />
        )}
      </div>
    </Modal>
  );
};
